import React, { useState } from 'react';
import { FileText, Check, X, Clock, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Sub-components ----

const QuoteStatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pendiente', color: 'text-amber-700 dark:text-amber-400 border-amber-500/30 bg-amber-500/10', icon: Clock },
    accepted: { label: 'Aceptada', color: 'text-emerald-700 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10', icon: Check },
    rejected: { label: 'Rechazada', color: 'text-red-600 dark:text-red-400 border-red-500/30 bg-red-500/10', icon: X },
  };
  const sc = config[status] || config.pending;
  const StatusIcon = sc.icon;
  return (
    <Badge variant="outline" className={cn("text-xs", sc.color)}>
      <StatusIcon className="w-3 h-3 mr-1" />
      {sc.label}
    </Badge>
  );
};

const QuoteHeader = ({ title, status, expanded, onToggle }) => (
  <div className="px-4 py-3 flex items-center justify-between bg-brand-copper/10 border-b border-amber-500/20">
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-lg bg-brand-copper flex items-center justify-center">
        <FileText className="w-4 h-4 text-white" />
      </div>
      <div>
        <p className="text-sm font-semibold">{title || 'Cotizacion'}</p>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Sparkles className="w-3 h-3 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
          <span>Generada por Noah IA</span>
        </div>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <QuoteStatusBadge status={status} />
      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onToggle}>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </Button>
    </div>
  </div>
);

const QuoteLineItems = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg overflow-hidden border border-border/50">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 text-xs text-muted-foreground">
            <th className="text-left p-2 font-medium">Producto</th>
            <th className="text-center p-2 font-medium">Cant.</th>
            <th className="text-right p-2 font-medium">P. Unit.</th>
            <th className="text-right p-2 font-medium">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.nombre + i} className="border-t border-border/30">
              <td className="p-2">
                <p className="font-medium text-sm">{item.nombre}</p>
                {item.descripcion && <p className="text-xs text-muted-foreground">{item.descripcion}</p>}
              </td>
              <td className="text-center p-2">{item.cantidad}</td>
              <td className="text-right p-2">${item.precio_unitario.toLocaleString()}</td>
              <td className="text-right p-2 font-medium">${item.subtotal.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const QuoteTotals = ({ quote }) => (
  <div className="space-y-1 pt-2 border-t border-border/30">
    {quote.subtotal > 0 && (
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Subtotal</span>
        <span>${quote.subtotal.toLocaleString()} {quote.moneda}</span>
      </div>
    )}
    {quote.descuento > 0 && (
      <div className="flex justify-between text-sm text-emerald-700 dark:text-emerald-400">
        <span>Descuento ({quote.descuento}%)</span>
        <span>-${((quote.subtotal * quote.descuento) / 100).toLocaleString()}</span>
      </div>
    )}
    {quote.impuesto > 0 && (
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">IVA ({quote.impuesto}%)</span>
        <span>${((quote.subtotal * (1 - quote.descuento / 100) * quote.impuesto) / 100).toLocaleString()}</span>
      </div>
    )}
    <div className="flex justify-between pt-2 border-t border-border/50">
      <span className="font-bold text-base">Total</span>
      <span className="font-bold text-lg text-amber-700 dark:text-amber-400">${quote.total.toLocaleString()} {quote.moneda}</span>
    </div>
  </div>
);

const QuoteActions = ({ status, onAccept, onReject }) => {
  if (status !== 'pending') return null;
  return (
    <div className="flex gap-2 pt-2">
      <Button
        data-testid="quote-accept-btn"
        size="sm"
        className="flex-1 bg-primary hover:opacity-90 text-white"
        onClick={onAccept}
      >
        <Check className="w-4 h-4 mr-1" /> Aceptar
      </Button>
      <Button
        data-testid="quote-reject-btn"
        variant="outline"
        size="sm"
        className="flex-1 border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/10"
        onClick={onReject}
      >
        <X className="w-4 h-4 mr-1" /> Rechazar
      </Button>
    </div>
  );
};

// ---- Parser ----

const parseQuoteText = (raw) => {
  const lines = raw.split('\n');
  const quote = { items: [], titulo: '', subtotal: 0, descuento: 0, impuesto: 0, total: 0, moneda: 'USD', nota: '', validez: '' };
  let inItems = false;
  let currentItem = null;

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.startsWith('titulo:')) {
      quote.titulo = trimmed.substring(7).trim();
      continue;
    }
    if (trimmed === 'items:') {
      inItems = true;
      continue;
    }

    if (inItems) {
      if (trimmed.startsWith('- nombre:')) {
        if (currentItem) quote.items.push(currentItem);
        currentItem = { nombre: trimmed.substring(9).trim(), descripcion: '', cantidad: 1, precio_unitario: 0, subtotal: 0 };
      } else if (currentItem && trimmed.startsWith('descripcion:')) {
        currentItem.descripcion = trimmed.substring(12).trim();
      } else if (currentItem && trimmed.startsWith('cantidad:')) {
        currentItem.cantidad = parseInt(trimmed.substring(9).trim()) || 1;
      } else if (currentItem && trimmed.startsWith('precio_unitario:')) {
        currentItem.precio_unitario = parseFloat(trimmed.substring(16).trim().replace(/[^\d.]/g, '')) || 0;
      } else if (currentItem && trimmed.startsWith('subtotal:')) {
        currentItem.subtotal = parseFloat(trimmed.substring(9).trim().replace(/[^\d.]/g, '')) || 0;
      } else if (!trimmed.startsWith('-') && !currentItem) {
        // End of items section
        inItems = false;
      }
    }

    if (!inItems) {
      if (currentItem) { quote.items.push(currentItem); currentItem = null; }
      if (trimmed.startsWith('subtotal:')) quote.subtotal = parseFloat(trimmed.substring(9).trim().replace(/[^\d.]/g, '')) || 0;
      else if (trimmed.startsWith('descuento:')) quote.descuento = parseFloat(trimmed.substring(10).trim().replace(/[^\d.]/g, '')) || 0;
      else if (trimmed.startsWith('impuesto:')) quote.impuesto = parseFloat(trimmed.substring(9).trim().replace(/[^\d.]/g, '')) || 0;
      else if (trimmed.startsWith('total:')) quote.total = parseFloat(trimmed.substring(6).trim().replace(/[^\d.]/g, '')) || 0;
      else if (trimmed.startsWith('moneda:')) quote.moneda = trimmed.substring(7).trim();
      else if (trimmed.startsWith('nota:')) quote.nota = trimmed.substring(5).trim();
      else if (trimmed.startsWith('validez:')) quote.validez = trimmed.substring(8).trim();
    }
  }
  if (currentItem) quote.items.push(currentItem);
  return quote;
};

// ---- Main Component ----

export const QuoteCard = ({ rawText, quoteId, onStatusChange }) => {
  const [expanded, setExpanded] = useState(true);
  const [status, setStatus] = useState('pending');
  const quote = parseQuoteText(rawText);

  const handleAction = async (newStatus) => {
    if (quoteId) {
      try {
        await axios.put(`${API}/quotes/${quoteId}/status`, null, { params: { status: newStatus } });
        toast.success(newStatus === 'accepted' ? 'Cotizacion aceptada' : 'Cotizacion rechazada');
      } catch {
        toast.error('Error al actualizar');
        return;
      }
    }
    setStatus(newStatus);
    onStatusChange?.(quoteId, newStatus);
  };

  return (
    <Card data-testid="quote-card" className={cn(
      "overflow-hidden border-2 transition-all duration-300",
      "bg-brand-copper/5",
      status === 'accepted' ? "border-emerald-500/40" : status === 'rejected' ? "border-red-500/40" : "border-amber-500/40"
    )}>
      <QuoteHeader title={quote.titulo} status={status} expanded={expanded} onToggle={() => setExpanded(!expanded)} />

      {expanded && (
        <CardContent className="p-4 space-y-3">
          <QuoteLineItems items={quote.items} />
          <QuoteTotals quote={quote} />

          {quote.nota && (
            <p className="text-xs text-muted-foreground italic bg-muted/30 rounded-lg p-2">{quote.nota}</p>
          )}
          {quote.validez && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" /> Validez: {quote.validez}
            </p>
          )}

          <QuoteActions status={status} onAccept={() => handleAction('accepted')} onReject={() => handleAction('rejected')} />
        </CardContent>
      )}
    </Card>
  );
};

/**
 * Splits a message content into text parts and quote blocks.
 * Returns array of { type: 'text'|'quote', content: string }
 */
export const parseMessageForQuotes = (content) => {
  if (!content) return [{ type: 'text', content: '' }];
  const parts = [];
  const regex = /\[COTIZACION\](.*?)\[\/COTIZACION\]/gs;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.substring(lastIndex, match.index).trim() });
    }
    parts.push({ type: 'quote', content: match[1].trim() });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < content.length) {
    const remaining = content.substring(lastIndex).trim();
    if (remaining) parts.push({ type: 'text', content: remaining });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content }];
};
